import { VenuePortalUser } from "@/graphql/operations";
import useLocalization from "@/hooks/useLocalization";
import useNotification from "@/hooks/useNotification";
import useSettings from "@/hooks/useSettings";
import { growthbook } from "@/setup/growthBook";
import { sendGaEvent, setGaUserId } from "@/tools/analytics";
import {
  clearJwtToken,
  fetchUser,
  setJwtToken,
} from "@/tools/jwtToken/jwtToken";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import * as Sentry from "@sentry/react";
import { VenuePortalError } from "@shared/errors";
import { sum } from "mt-shared/collections/sum";
import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  EDIT_PASSWORD_MUTATION,
  EditPasswordMutationInput,
  EditPasswordMutationResponse,
} from "./mutations/editPasswordMutation";
import {
  LOGIN_MUTATION,
  LoginMutationInput,
  LoginMutationResponse,
} from "./mutations/loginMutation";
import {
  MARK_VENUE_PORTAL_USER_NOTIFICATIONS_READ_MUTATION,
  MarkVenuePortalUserNotificationsReadInput,
  MarkVenuePortalUserNotificationsReadResponse,
} from "./mutations/markVenuePortalUserNotificationsReadMutation";
import {
  REQUEST_PASSWORD_MUTATION,
  RequestPasswordInput,
  RequestPasswordReponse,
} from "./mutations/requestPasswordMutation";
import {
  GET_PASSWORD_REQUEST_QUERY,
  GetPasswordRequestQueryInput,
  GetPasswordRequestQueryResponse,
} from "./queries/getRequestPasswordEmailQuery";
import {
  GET_VENUE_PORTAL_USER_NOTIFICATIONS_QUERY,
  GetVenuePortalUserNotificationsInput,
  GetVenuePortalUserNotificationsResponse,
} from "./queries/getVenuePortalUserNotificationsQuery";

function useUserValues() {
  const { t } = useLocalization();
  const { addNotification } = useNotification();

  const [user, setUser] = useState(fetchUser());
  const { setUserSettingsId, settings } = useSettings();

  const impersonateUser = useCallback(
    (id: VenuePortalUser["id"] | undefined) => {
      setUser((user) => ({ ...user, userId: id }));
    },
    [setUser],
  );

  const updateUser = useCallback(() => {
    const user = fetchUser();
    setUser(user);
    setUserSettingsId(user.userId ?? null);
    setGaUserId(user.userId ?? null);
    if (!user.userId) return;
    growthbook.setAttributes(user);
    Sentry.setUser({ id: user.userId });
  }, [setUserSettingsId]);

  useEffect(() => {
    updateUser();
  }, [updateUser]);

  const [loginMutation] = useMutation<
    LoginMutationResponse,
    LoginMutationInput
  >(LOGIN_MUTATION);

  const [editPasswordMutation] = useMutation<
    EditPasswordMutationResponse,
    EditPasswordMutationInput
  >(EDIT_PASSWORD_MUTATION);

  const [requestPasswordMutation] = useMutation<
    RequestPasswordReponse,
    RequestPasswordInput
  >(REQUEST_PASSWORD_MUTATION);

  const [getRequestPasswordEmailQuery] = useLazyQuery<
    GetPasswordRequestQueryResponse,
    GetPasswordRequestQueryInput
  >(GET_PASSWORD_REQUEST_QUERY);

  const { data: notificationsData, loading: notificationsLoading } = useQuery<
    GetVenuePortalUserNotificationsResponse,
    GetVenuePortalUserNotificationsInput
  >(GET_VENUE_PORTAL_USER_NOTIFICATIONS_QUERY, {
    variables: {
      venuePortalUserId: user.userId ?? "",
      iso639_1: settings.iso639_1 ?? "en",
    },
    skip: !user.userId,
  });

  const notificationLinks = useMemo(
    () => notificationsData?.venuePortalUser.notificationLinks ?? [],
    [notificationsData],
  );

  const numUnreadNotifications = useMemo(
    () => sum(notificationLinks.map((link) => Number(!link.readAt))),
    [notificationLinks],
  );

  const [markNotificationsReadMutation] = useMutation<
    MarkVenuePortalUserNotificationsReadResponse,
    MarkVenuePortalUserNotificationsReadInput
  >(MARK_VENUE_PORTAL_USER_NOTIFICATIONS_READ_MUTATION);

  const login = useCallback(
    async (email: string, password: string, onCompleted?: () => void) => {
      const { data } = await loginMutation({
        variables: {
          email,
          password,
        },
      });
      if (data?.login.jwtToken) {
        setJwtToken(data.login.jwtToken);
        updateUser();
        sendGaEvent("login");
        addNotification({
          severity: "success",
          message: t("notification:login.loggedIn"),
        });
        onCompleted?.();
      }

      if (data?.login.error) {
        addNotification({
          severity: "error",
          message: `${t("notification:login.failedToLogin")}: ${t(
            `error:${data.login.error}`,
          )}`,
        });
      }

      return { error: data?.login.error };
    },
    [loginMutation, updateUser, addNotification, t],
  );

  const logout = useCallback(() => {
    clearJwtToken();
    updateUser();
    location.reload();
    addNotification({
      severity: "success",
      message: t("notification:login.loggedOut"),
    });
  }, [updateUser, addNotification, t]);

  const editPassword = useCallback(
    async (variables: EditPasswordMutationInput, onCompleted?: () => void) => {
      await editPasswordMutation({
        variables,
        onCompleted() {
          addNotification({
            severity: "success",
            message: t("notification:password.savedPassword"),
          });
          onCompleted?.();
        },
        onError(error) {
          addNotification({
            severity: "error",
            message: `${t("notification:password.failedToSavePassword")}: ${t(
              `error:${error.message as VenuePortalError}`,
            )}`,
          });
        },
      });
    },
    [editPasswordMutation, addNotification, t],
  );

  const requestPassword = useCallback(
    async (variables: RequestPasswordInput, onCompleted?: () => void) => {
      await requestPasswordMutation({
        variables,
        onCompleted() {
          addNotification({
            severity: "success",
            message: t("notification:password.sentPasswordResetEmail"),
          });
          onCompleted?.();
        },
        onError(error) {
          addNotification({
            severity: "error",
            message: `${t(
              "notification:password.failedToSendPasswordResetEmail",
            )}: ${t(`error:${error.message as VenuePortalError}`)}`,
          });
        },
      });
    },
    [requestPasswordMutation, addNotification, t],
  );

  const getRequestPasswordEmail = useCallback(
    async (variables: GetPasswordRequestQueryInput) => {
      const { data, loading, error } = await getRequestPasswordEmailQuery({
        variables,
      });
      return {
        isUsed: !!data?.venuePortalPasswordRequest?.usedAt,
        isExpired: data?.venuePortalPasswordRequest?.isExpired,
        email: data?.venuePortalPasswordRequest?.user?.email,
        iso639_1: data?.venuePortalPasswordRequest?.user?.settings.iso639_1,
        loading,
        error,
      };
    },
    [getRequestPasswordEmailQuery],
  );

  const markNotificationsRead = useCallback(async () => {
    if (user.mtId || !user.userId) return;
    const readAt = new Date().toISOString();
    await markNotificationsReadMutation({
      variables: { venuePortalUserId: user.userId },
      optimisticResponse: { markVenuePortalUserNotificationsRead: true },
      update: (cache) => {
        cache.updateQuery<
          GetVenuePortalUserNotificationsResponse,
          GetVenuePortalUserNotificationsInput
        >(
          {
            query: GET_VENUE_PORTAL_USER_NOTIFICATIONS_QUERY,
            variables: {
              venuePortalUserId: user.userId!,
              iso639_1: settings.iso639_1 ?? "en",
            },
          },
          (data) => {
            if (!data?.venuePortalUser) return data;
            return {
              venuePortalUser: {
                ...data.venuePortalUser,
                notificationLinks: data.venuePortalUser.notificationLinks.map(
                  (link) => (link.readAt ? link : { ...link, readAt: readAt }),
                ),
              },
            };
          },
        );
      },
    });
  }, [
    markNotificationsReadMutation,
    settings.iso639_1,
    user.userId,
    user.mtId,
  ]);

  const isMtUser = useMemo(() => !!user.mtId, [user.mtId]);
  const isSubUser = useMemo(() => !!user.parentId, [user.parentId]);

  return {
    editPassword,
    getRequestPasswordEmail,
    impersonateUser,
    isMtUser,
    isSubUser,
    login,
    notificationLinks,
    notificationsLoading,
    numUnreadNotifications,
    logout,
    markNotificationsRead,
    requestPassword,
    user,
  };
}

export const UserContext = createContext<ReturnType<
  typeof useUserValues
> | null>(null);

export default function UserProvider({ children }: React.PropsWithChildren) {
  return (
    <UserContext.Provider value={useUserValues()}>
      {children}
    </UserContext.Provider>
  );
}
